import Grid from '@material-ui/core/Grid'
import { isEqual } from 'lodash/fp'
import Typography from '@material-ui/core/Typography'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import TextField from '../../components/TextField'
import LabelMessage from '../../components/LabelMessage'
import {
  personNameFilter,
  Locale,
  isoDateStrToDateOrNull,
  dateToISO,
  getAffectedPersonCode,
  auxAffectedPersonCode,
} from '../../helpers/'
import theme from '../../theme'
import BanButton from '../../components/BanButton'
import DeleteIcon from '@material-ui/icons/Delete'
import ButtonSimply from '../../components/ButtonSimply'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import CalendarLogo from '../../components/LogoCalendar'
import DateTextField from '../../components/DateTextField'
import FormControl from '@material-ui/core/FormControl'
import FormLabel from '@material-ui/core/FormLabel'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import { ErrorMessage, FieldArray, Form, Formik, getIn } from 'formik'
import {
  AttachmentListRespType,
  AttachmentRespType,
  AttachmentType,
  CaseCommonDataType,
  CaseReportId,
  InsuredPersonType,
  LookupItemType,
  PartType,
} from '../../models'
import { useStyles } from '../../pages/styled'
import { Attachments, AttachmentsChangedEvent } from '../Attachments'
import {
  combineAttachmentsList,
  getAttachmentsFromAttachmentListOrEmpty,
} from '../../models/attachmentListFnc'
import { DestinationPicker } from '../DestinationPicker'
import * as yup from 'yup'
import { DatePicker } from '../DatePicker'
import { LookupItemPicker } from '../LookupItemPicker'
import { QuestionMark } from '../QuestionMark'
import ErrorSubmitBack from '../ErrorSubmitBack'
import { Ga4Form } from './Ga4Form'
import { RadioButtonsField } from '../RadioButtonsField'
import { objectToLookup } from '../../helpers/miscFunctions'

const InsuredPersonInitialValues = {
  orderNum: 1,
  birthDate: null,
}

export type CancellationFormDataType = {
  caseCommon?: CaseCommonDataType | null
  caseCA?: null | {
    // subset of CaseCARespType
    reservationDate?: string | null
    firstPaymentDate?: string | null
    reason?: string
    insuredRelation?: string
    // typ ma byt PersonDataType a ne InsuredPersonType -> TODO refaktorovat
    insuredPersons?: InsuredPersonType[]
    insuredPersonExists?: boolean
    // TODO attachments v dalším kroku
    attachmentList?: AttachmentListRespType[]
  }
}

const CancellationForm = (params: {
  caseReportId: CaseReportId
  cancellationData: CancellationFormDataType
  onPageContinue: (cancelData: CancellationFormDataType) => void
  onPageBack: () => void
  isContinueInProgress: boolean
  isDataLoading?: boolean
  errorMessage: string
  setIfEqual: (isChanged: boolean) => void
}) => {
  const classes = useStyles(theme)
  const { t } = useTranslation()
  const lang = t('appl.header.language') === 'EN' ? 'en' : 'pl'

  type CancellationFormikType = {
    reservationDate: Date | null
    destination: string | null // code
    firstPaymentDate: Date | null
    reason: string | null // code
    occurredOn: Date | null
    affectedPerson: string | null // code
    relation: string | null // code
    description: string
    insuredPersonExists: boolean
    insuredPersons: InsuredPersonType[]
    attachmentsReceipt: AttachmentRespType[]
    attachmentsSupport: AttachmentRespType[]
  }

  // INICIALIZACE FORMULARE
  const transformInputToFormikValues = (
    values: CancellationFormDataType
  ): CancellationFormikType => {
    return {
      reservationDate: isoDateStrToDateOrNull(values?.caseCA?.reservationDate),
      destination: values?.caseCommon?.destination ?? null,
      firstPaymentDate: isoDateStrToDateOrNull(
        values?.caseCA?.firstPaymentDate
      ),
      reason: values?.caseCA?.reason ?? null,
      occurredOn: isoDateStrToDateOrNull(values?.caseCommon?.occurredOn),
      // insuredRelation transformation to two fields
      affectedPerson: getAffectedPersonCode(values?.caseCA?.insuredRelation),
      relation:
        values?.caseCA?.insuredRelation == null
          ? null
          : getAffectedPersonCode(values?.caseCA?.insuredRelation) ===
            auxAffectedPersonCode
            ? values?.caseCA?.insuredRelation
            : null,
      description: values?.caseCommon?.description ?? '',
      insuredPersonExists:
        values.caseCA?.insuredPersons?.length === 0 ? false : true,
      insuredPersons: values?.caseCA?.insuredPersons ?? [],
      attachmentsReceipt: getAttachmentsFromAttachmentListOrEmpty(
        values.caseCA?.attachmentList,
        AttachmentType.RECEIPT
      ),
      attachmentsSupport: getAttachmentsFromAttachmentListOrEmpty(
        values.caseCA?.attachmentList,
        AttachmentType.SUPPORT
      ),
    }
  }

  const handleFormSubmit = (values: any) => {
    params.onPageContinue(transformFormikValueToCancellationData(values))
  }

  function transformFormikValueToCancellationData(
    values: CancellationFormikType
  ): CancellationFormDataType {
    const editedAttachmentList: AttachmentListRespType[] = [
      {
        attachmentType: AttachmentType.RECEIPT,
        attachments: values.attachmentsReceipt,
      },
      {
        attachmentType: AttachmentType.SUPPORT,
        attachments: values.attachmentsSupport,
      },
    ]

    const insuredPersonsTransformed = values.insuredPersons?.map(
      (insuredPerson: any, index) => {
        return {
          orderNum: index + 1,
          firstName: insuredPerson.firstName,
          lastName: insuredPerson.lastName,
          birthDate: dateToISO(insuredPerson.birthDate),
          insuredRelation: insuredPerson.insuredRelation,
        }
      }
    )

    return {
      // NAPLNIT DATY Z FORMULARE
      caseCommon: {
        occurredOn: dateToISO(values.occurredOn),
        description: values.description,
        destination: values.destination,
      },
      caseCA: {
        reservationDate: dateToISO(values.reservationDate),
        firstPaymentDate: dateToISO(values.firstPaymentDate),
        reason: values.reason ?? undefined,
        // transform two fields to insuredRelation
        insuredRelation:
          values.affectedPerson !== auxAffectedPersonCode
            ? values.affectedPerson ?? undefined
            : values.relation ?? undefined,
        insuredPersons: insuredPersonsTransformed,
        insuredPersonExists: values.insuredPersonExists,
        attachmentList: combineAttachmentsList(
          params.cancellationData.caseCA?.attachmentList, // original
          editedAttachmentList // porizene
        ),
      },
    }
  }

  const handleBackButtonClicked = () => {
    params.onPageBack()
  }

  const CancellationSchema = () =>
    yup.object().shape({
      reservationDate: yup
        .date()
        .nullable()
        .required(t('dateMessage.required'))
        .min(new Date('1999-12-31'), t('dateMessage.err2'))
        .max(new Date(), t('dateMessage.err2'))
        .typeError(t('dateMessage.err1')),
      destination: yup
        .string()
        .nullable()
        .required(t('thirdPg.destination.required')),
      firstPaymentDate: yup
        .date()
        .nullable()
        .min(new Date('1999-12-31'), t('dateMessage.err2'))
        .min(
          yup.ref('reservationDate'),
          t('cancellationPg.firstPaymentDate.err')
        )
        .required(t('dateMessage.required'))
        .max(new Date(), t('dateMessage.err2'))
        .typeError(t('dateMessage.err1')),
      reason: yup
        .string()
        .nullable()
        .required(t('cancellationPg.reason.required')),
      occurredOn: yup
        .date()
        .nullable()
        .required(t('dateMessage.required'))
        .min(new Date('1999-12-31'), t('dateMessage.err2'))
        .min(
          yup.ref('reservationDate'),
          t('cancellationPg.occurredOn.errBeforeReservation')
        )
        .min(
          yup.ref('firstPaymentDate'),
          t('cancellationPg.occurredOn.errBeforePayment')
        )
        .max(new Date(), t('dateMessage.err2'))
        .typeError(t('dateMessage.err1')),
      affectedPerson: yup.string().nullable().required(t('optionRequired')),
      relation: yup
        .string()
        .nullable()
        .when('affectedPerson', {
          is: (affectedPerson: any) => affectedPerson === auxAffectedPersonCode,
          then: yup
            .string()
            .required(t('cancellationPg.relation.required'))
            .nullable(),
        }),
      insuredPersons: yup.array().of(
        yup.object().shape({
          firstName: yup
            .string()
            .required(t('firstPg.firstName.required'))
            .min(2, t('lengthText')),
          lastName: yup
            .string()
            .required(t('firstPg.lastName.required'))
            .min(2, t('lengthText')),
          insuredRelation: yup
            .string()
            .nullable()
            .required(t('cancellationPg.relation.required')),
          birthDate: yup
            .date()
            .nullable()
            .required(t('firstPg.birthDate.required'))
            .min(new Date('1899-12-31'), t('dateMessage.err2'))
            .max(new Date(), t('dateMessage.err2'))
            .typeError(t('dateMessage.err1')),
        })
      ),
      attachmentsReceipt: yup.array().nullable().min(1, t('uploadFile.err1')),
      attachmentsSupport: yup.array().nullable().min(1, t('uploadFile.err1')),
    })

  const formikRef = useRef(null)
  return (
    <Ga4Form formName={'CancellationForm'} >
      <Formik
        initialValues={transformInputToFormikValues(params.cancellationData)}
        validationSchema={CancellationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
        innerRef={formikRef}
      >
        {({
          initialValues,
          values,
          errors,
          setFieldValue,
          touched,
          handleBlur,
          handleChange,
        }) => {
          const isChanged = isEqual(values, initialValues)
          params.setIfEqual(isChanged)
          return (
            <Form className={classes.root}>
              <Grid
                item
                sm={12}
                xl={12}
                xs={12}
                container
                direction="row"
                className={classes.gridMainForm}
              >
                <Grid
                  item
                  sm={9}
                  xl={9}
                  xs={12}
                  container
                  direction="row"
                  className={classes.gridWorkArea}
                >
                  <Grid
                    item
                    sm={12}
                    xs={12}
                    xl={12}
                    container
                    className={classes.gridResize}
                  >
                    <Typography className={classes.text} variant="h3">
                      {t('cancellationPg.title')}

                      <QuestionMark
                        text={t('cancellationPg.questionMarkLabel')}
                        position={'5px'}
                        height={20}
                      />
                    </Typography>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={6} xs={6} sm={6}>
                        <DatePicker
                          name="reservationDate"
                          label={
                            <>
                              {t('cancellationPg.reservationDate.label')}
                              <QuestionMark
                                text={t(
                                  'cancellationPg.reservationDate.questionMarkLabel'
                                )}
                              />
                            </>
                          }
                          error={Boolean(
                            errors.reservationDate && touched.reservationDate
                          )}
                          helperText={
                            errors.reservationDate && touched.reservationDate
                              ? errors.reservationDate
                              : null
                          }
                          required={true}
                          maxDate={new Date()}
                          minDate={new Date(2000, 0, 1)}
                          value={values.reservationDate}
                          onChange={(value) => {
                            setFieldValue('reservationDate', value)
                          }}
                        />
                      </Grid>
                      <Grid item sm={6} xl={6} xs={6}>
                        <DestinationPicker
                          name={'destination'}
                          label={t('cancellationPg.destination.label')}
                          selectedItemCode={values.destination}
                          onValueChanged={(
                            selectedDestination: string | null
                          ) => {
                            setFieldValue('destination', selectedDestination)
                          }}
                          onBlur={handleBlur}
                          error={Boolean(
                            errors.destination && touched.destination
                          )}
                          helperText={
                            errors.destination && touched.destination
                              ? errors.destination
                              : null
                          }
                          required
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        xl={12}
                        xs={12}
                        sm={12}
                        className={classes.fieldSize}
                      >
                        <DatePicker
                          name="firstPaymentDate"
                          label={t('cancellationPg.firstPaymentDate.label')}
                          error={Boolean(
                            errors.firstPaymentDate && touched.firstPaymentDate
                          )}
                          helperText={
                            errors.firstPaymentDate && touched.firstPaymentDate
                              ? errors.firstPaymentDate
                              : null
                          }
                          required={true}
                          maxDate={new Date()}
                          minDate={new Date(2000, 0, 1)}
                          value={values.firstPaymentDate}
                          onChange={(value) => {
                            setFieldValue('firstPaymentDate', value)
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.gridResize}
                      >
                        <Attachments
                          name={'attachmentsReceipt'}
                          caseReportId={params.caseReportId}
                          labelText={t('uploadAttachments.CASE.RECEIPT')}
                          attachmentType={AttachmentType.RECEIPT}
                          part={PartType.CASE}
                          attachments={values.attachmentsReceipt}
                          onAttachmentsChanged={(
                            event: AttachmentsChangedEvent
                          ) => {
                            setFieldValue(
                              'attachmentsReceipt',
                              event.value.attachments
                            )
                          }}
                          onBlur={handleBlur}
                          required
                          error={
                            getIn(errors, 'attachmentsReceipt') &&
                            getIn(touched, 'attachmentsReceipt')
                          }
                          helperText={
                            getIn(errors, 'attachmentsReceipt') &&
                              getIn(touched, 'attachmentsReceipt')
                              ? getIn(errors, 'attachmentsReceipt')
                              : null
                          }
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        xl={12}
                        xs={12}
                        sm={12}
                        className={classes.fieldSize}
                      >
                        <LookupItemPicker
                          name={`reason`}
                          label={t('cancellationPg.reason.label')}
                          lookupItems={t<string, LookupItemType[]>(`reasons`, {
                            defaultValue: [] /* returned if not found */,
                            returnObjects:
                              true /* array of {code:string, name:string} */,
                          })}
                          selectedItemCode={values.reason ?? null}
                          onValueChanged={(newValue: string | null) => {
                            setFieldValue(`reason`, newValue)
                          }}
                          onBlur={handleBlur}
                          error={Boolean(errors.reason && touched.reason)}
                          helperText={
                            errors.reason && touched.reason ? errors.reason : null
                          }
                          required
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        xl={12}
                        xs={12}
                        sm={12}
                        className={classes.fieldSize}
                      >
                        <DatePicker
                          label={t('thirdPg.occurredOn.label')}
                          name="occurredOn"
                          helperText={
                            errors.occurredOn && touched.occurredOn
                              ? errors.occurredOn
                              : null
                          }
                          error={Boolean(errors.occurredOn && touched.occurredOn)}
                          required={true}
                          value={values.occurredOn}
                          onChange={(value) => {
                            setFieldValue('occurredOn', value)
                          }}
                          minDate={new Date('2000, 0, 1')}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <RadioButtonsField
                          aria-label={'affectedPerson'}
                          name={'affectedPerson'}
                          label={t('cancellationPg.affectedPerson.label')}
                          required
                          value={values.affectedPerson}
                          onChange={handleChange}
                          lookup={objectToLookup(t('cancellationPg.affectedPerson.code', { returnObjects: true }))}
                          error={Boolean(errors.affectedPerson && touched.affectedPerson)}
                          helperText={errors.affectedPerson && touched.affectedPerson
                            ? errors.affectedPerson
                            : null}
                        />
                      </Grid>
                    </Grid>
                    {values.affectedPerson === auxAffectedPersonCode && (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        <Grid
                          item
                          xl={12}
                          xs={12}
                          sm={12}
                          className={classes.fieldSize}
                        >
                          <LookupItemPicker
                            name={`relation`}
                            label={t('cancellationPg.relation.label')}
                            lookupItems={t<string, LookupItemType[]>(
                              `relations`,
                              {
                                defaultValue: [] /* returned if not found */,
                                returnObjects:
                                  true /* array of {code:string, name:string} */,
                              }
                            )}
                            selectedItemCode={values.relation ?? null}
                            onValueChanged={(newValue: string | null) => {
                              setFieldValue(`relation`, newValue)
                            }}
                            helperText={
                              errors.relation && touched.relation
                                ? errors.relation
                                : null
                            }
                            error={Boolean(errors.relation && touched.relation)}
                            required={true}
                            onBlur={handleBlur}
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.gridResize}
                      >
                        <FormLabel className={classes.textAreaLabel}>
                          {t('cancellationPg.description.label')}
                          <QuestionMark
                            text={t(
                              'cancellationPg.description.questionMarkLabel'
                            )}
                            position={'0px'}
                          />
                        </FormLabel>
                        <TextField
                          aria-label="description"
                          name="description"
                          /* label={
                            <span>
                              {t('cancellationPg.description.label')}
                              <QuestionMark
                                text={t(
                                  'cancellationPg.description.questionMarkLabel'
                                )}
                              />
                            </span>
                          } */
                          value={values.description}
                          onChange={handleChange}
                          multiline
                          rows={7}
                          rowsMax={7}
                          placeholder={t('cancellationPg.description.hint')}
                          className={classes.textAreaClass}
                        />

                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.gridResize}
                      >
                        <Attachments
                          name={'attachmentsSupport'}
                          caseReportId={params.caseReportId}
                          labelText={t('uploadAttachments.CASE.SUPPORTCA')}
                          attachmentType={AttachmentType.SUPPORT}
                          part={PartType.CASE}
                          attachments={values.attachmentsSupport}
                          onAttachmentsChanged={(
                            event: AttachmentsChangedEvent
                          ) => {
                            setFieldValue(
                              'attachmentsSupport',
                              event.value.attachments
                            )
                          }}
                          onBlur={handleBlur}
                          required={true}
                          error={
                            getIn(errors, 'attachmentsSupport') &&
                            getIn(touched, 'attachmentsSupport')
                          }
                          helperText={
                            getIn(errors, 'attachmentsSupport') &&
                              getIn(touched, 'attachmentsSupport')
                              ? getIn(errors, 'attachmentsSupport')
                              : null
                          }
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12}>
                        <FormControl component="fieldset">
                          <FormControlLabel
                            name="insuredPersonExists"
                            value={values.insuredPersonExists}
                            control={<Checkbox />}
                            label={t('cancellationPg.insuredPersonExists.label')}
                            checked={values.insuredPersonExists}
                            onChange={() => {
                              setFieldValue(
                                'insuredPersons',
                                !values.insuredPersonExists
                                  ? [InsuredPersonInitialValues]
                                  : []
                              )
                              setFieldValue(
                                'insuredPersonExists',
                                !values.insuredPersonExists
                              )
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                    {values.insuredPersonExists && (
                      <FieldArray
                        name="insuredPersons"
                        render={({ push, remove }) => (
                          <React.Fragment>
                            {values?.insuredPersons &&
                              Boolean(values?.insuredPersons?.length) &&
                              values?.insuredPersons?.map(
                                (insuredPerson: any, index: number) => {
                                  return (
                                    <React.Fragment key={index}>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Grid
                                          item
                                          xl={12}
                                          xs={12}
                                          sm={12}
                                          className={classes.rowAlign}
                                        >
                                          <Typography
                                            className={classes.text}
                                            variant="h3"
                                          >
                                            {index +
                                              2 +
                                              '. ' +
                                              t(
                                                'cancellationPg.insuredPersons.title'
                                              )}
                                          </Typography>
                                          {index ===
                                            (values?.insuredPersons &&
                                              values?.insuredPersons?.length -
                                              1) && (
                                              <BanButton
                                                onClick={() => {
                                                  remove(index)
                                                }}
                                              >
                                                <DeleteIcon />
                                              </BanButton>
                                            )}
                                        </Grid>
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <LabelMessage
                                            name={`insuredPersons.${index}.firstName`}
                                            touched={touched}
                                            errors={errors}
                                            text={t('firstPg.firstName.label')}
                                          />
                                          <TextField
                                            regex={personNameFilter}
                                            placeholder={t(
                                              'firstPg.firstName.hint'
                                            )}
                                            name={`insuredPersons[${index}].firstName`}
                                            onChange={handleChange}
                                            value={
                                              values.insuredPersons &&
                                              values.insuredPersons[index]
                                                ?.firstName
                                            }
                                            className={classes.fieldSize}
                                            InputProps={{ required: false }}
                                            inputProps={{
                                              maxLength: 59,
                                            }}
                                            onBlur={handleBlur}
                                            required
                                          />
                                          <ErrorMessage
                                            name={`insuredPersons.${index}.firstName`}
                                            component="div"
                                            className={classes.checkError}
                                          />
                                        </Grid>
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <LabelMessage
                                            name={`insuredPersons.${index}.lastName`}
                                            touched={touched}
                                            errors={errors}
                                            text={t('firstPg.lastName.label')}
                                          />
                                          <TextField
                                            regex={personNameFilter}
                                            //@ts-ignore
                                            placeholder={t(
                                              'firstPg.lastName.hint'
                                            )}
                                            name={`insuredPersons[${index}].lastName`}
                                            onChange={handleChange}
                                            value={
                                              values.insuredPersons &&
                                              values.insuredPersons[index]
                                                ?.lastName
                                            }
                                            className={classes.fieldSize}
                                            InputProps={{ required: false }}
                                            inputProps={{
                                              maxLength: 59,
                                            }}
                                            onBlur={handleBlur}
                                            required
                                          />
                                          <ErrorMessage
                                            name={`insuredPersons.${index}.lastName`}
                                            component="div"
                                            className={classes.checkError}
                                          />
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Grid item xl={6} xs={6} sm={6}>
                                          <LabelMessage
                                            name={`insuredPersons.${index}.birthDate`}
                                            touched={touched}
                                            errors={errors}
                                            text={t('firstPg.birthDate.label')}
                                          />
                                          <LocalizationProvider
                                            locale={lang === 'en' ? `` : Locale}
                                            dateAdapter={AdapterDateFns}
                                          >
                                            <DesktopDatePicker
                                              renderInput={(props) => (
                                                <DateTextField
                                                  {...props}
                                                  name={`insuredPersons.${index}.birthDate`}
                                                  className={classes.fieldSize}
                                                  onBlur={handleBlur}
                                                  required
                                                />
                                              )}
                                              inputFormat="dd.MM.yyyy"
                                              //@ts-ignore
                                              autoOk={true}
                                              margin="normal"
                                              maxDate={new Date()}
                                              minDate={new Date(1900, 0, 1)}
                                              value={
                                                values.insuredPersons &&
                                                values.insuredPersons[index]
                                                  ?.birthDate
                                              }
                                              onChange={(value) => {
                                                setFieldValue(
                                                  `insuredPersons[${index}].birthDate`,
                                                  value
                                                )
                                              }}
                                              InputProps={{
                                                required: false,
                                              }}
                                              components={{
                                                OpenPickerIcon: CalendarLogo,
                                              }}
                                              disableHighlightToday={true}
                                              placeholder={t(
                                                'firstPg.birthDate.hint'
                                              )}
                                              disableMaskedInput={true}
                                            />
                                          </LocalizationProvider>
                                          <ErrorMessage
                                            name={`insuredPersons.${index}.birthDate`}
                                            component="div"
                                            className={classes.checkError}
                                          />
                                        </Grid>
                                        <Grid item xl={6} xs={6} sm={6}>
                                          <LookupItemPicker
                                            name={`insuredPersons.${index}.insuredRelation`}
                                            label={t(
                                              'cancellationPg.relation.label'
                                            )}
                                            lookupItems={t<
                                              string,
                                              LookupItemType[]
                                            >(`relations`, {
                                              defaultValue:
                                                [] /* returned if not found */,
                                              returnObjects:
                                                true /* array of {code:string, name:string} */,
                                            })}
                                            selectedItemCode={
                                              values.insuredPersons[index]
                                                .insuredRelation ?? null
                                            }
                                            onValueChanged={(
                                              newValue: string | null
                                            ) => {
                                              setFieldValue(
                                                `insuredPersons[${index}].insuredRelation`,
                                                newValue
                                              )
                                            }}
                                            error={
                                              getIn(
                                                errors,
                                                `insuredPersons[${index}].insuredRelation`
                                              ) &&
                                              getIn(
                                                touched,
                                                `insuredPersons[${index}].insuredRelation`
                                              )
                                            }
                                            helperText={
                                              getIn(
                                                errors,
                                                `insuredPersons[${index}].insuredRelation`
                                              ) &&
                                                getIn(
                                                  touched,
                                                  `insuredPersons[${index}].insuredRelation`
                                                )
                                                ? getIn(
                                                  errors,
                                                  `insuredPersons[${index}].insuredRelation`
                                                )
                                                : null
                                            }
                                            required={true}
                                            onBlur={handleBlur}
                                          />
                                        </Grid>
                                      </Grid>
                                    </React.Fragment>
                                  )
                                }
                              )}
                            <Grid
                              item
                              xl={12}
                              xs={12}
                              sm={12}
                              className={classes.row}
                            >
                              <Grid
                                item
                                xl={6}
                                xs={6}
                                sm={6}
                                className={classes.fieldSize}
                              >
                                <ButtonSimply
                                  onClick={() => {
                                    const initialValue = {
                                      ...InsuredPersonInitialValues,
                                      orderNum:
                                        values.insuredPersons &&
                                        values.insuredPersons.length + 1,
                                    }
                                    push(initialValue)
                                  }}
                                >
                                  {t('cancellationPg.addPerson')}
                                </ButtonSimply>
                              </Grid>
                            </Grid>
                          </React.Fragment>
                        )}
                      />
                    )}
                    <ErrorSubmitBack
                      formik={formikRef.current}
                      pending={params.isContinueInProgress}
                      backLinkText={t('thirdPg.backBtn')}
                      onBackLinkClick={handleBackButtonClicked}
                    >
                      {t('continueBtn')}
                    </ErrorSubmitBack>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Ga4Form >
  )
}

export default CancellationForm
